function toSafeNumber(value, fallback = 0) {
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : fallback;
}

function resolveTagProrationMode(setup = {}) {
  const explicitMode = setup?.membershipYearTagProrationMode;
  if (explicitMode === 'all' || explicitMode === 'none' || explicitMode === 'selected') {
    return explicitMode;
  }
  return setup?.membershipYearProrateTaggedPrices === false ? 'none' : 'all';
}

export function resolveCycleBasePricing({
  baseRate = 0,
  baseRateYearly,
  tagOverrides = {},
  tagOverridesYearly = {},
  appliedTags = [],
  preferredTag = null,
  cadenceUnit = 'month',
  cadenceCount = 1,
} = {}) {
  const unit = cadenceUnit === 'year' ? 'year' : 'month';
  const count = Math.max(1, toSafeNumber(cadenceCount, 1));
  const monthlyBase = toSafeNumber(baseRate, 0);
  const yearlyBase =
    baseRateYearly != null
      ? toSafeNumber(baseRateYearly, monthlyBase * 12)
      : monthlyBase * 12;

  const baseCandidate = unit === 'year' ? yearlyBase : monthlyBase;
  const candidates = [{ source: 'base', value: baseCandidate, tag: null }];
  for (const tag of appliedTags || []) {
    const overrideMonthly = tagOverrides?.[tag];
    const overrideYearly = tagOverridesYearly?.[tag];
    const ovMonthly = toSafeNumber(overrideMonthly, NaN);
    const ovYearly = toSafeNumber(overrideYearly, NaN);
    const resolvedOverride =
      unit === 'year'
        ? (Number.isFinite(ovYearly) ? ovYearly : (Number.isFinite(ovMonthly) ? ovMonthly * 12 : NaN))
        : (Number.isFinite(ovMonthly) ? ovMonthly : (Number.isFinite(ovYearly) ? ovYearly / 12 : NaN));
    if (!Number.isFinite(resolvedOverride)) continue;
    candidates.push({
      source: 'tag',
      value: resolvedOverride,
      tag,
    });
  }

  const normalizedPreferredTag = typeof preferredTag === 'string' ? preferredTag.trim() : '';
  if (normalizedPreferredTag) {
    const preferredCandidate = candidates.find((candidate) => candidate.tag === normalizedPreferredTag);
    if (preferredCandidate) {
      return {
        baseForCycle: preferredCandidate.value * count,
        bestForUnit: preferredCandidate.value,
        usedTagOverride: preferredCandidate.source === 'tag',
        tagName: preferredCandidate.source === 'tag' ? preferredCandidate.tag : null,
      };
    }
  }

  const best = candidates.reduce((winner, candidate) => (
    candidate.value < winner.value ? candidate : winner
  ), candidates[0]);

  return {
    baseForCycle: best.value * count,
    bestForUnit: best.value,
    usedTagOverride: best.source === 'tag',
    tagName: best.source === 'tag' ? best.tag : null,
  };
}

export function resolveCycleBaseMinor(args = {}) {
  return resolveCycleBasePricing(args).baseForCycle;
}

export function getYearlyBaseMinor({
  baseRate = 0,
  baseRateYearly,
} = {}) {
  const monthlyBase = toSafeNumber(baseRate, 0);
  if (baseRateYearly != null) return toSafeNumber(baseRateYearly, monthlyBase * 12);
  return monthlyBase * 12;
}

export function shouldProrateMembershipYear({
  setup = {},
  cadenceUnit = 'month',
  cadenceCount = 1,
  usedTagOverride = false,
  tagName = null,
} = {}) {
  const isYearlySingleCycle =
    cadenceUnit === 'year' && Math.max(1, toSafeNumber(cadenceCount, 1)) === 1;
  if (!setup?.membershipYearEnabled || !isYearlySingleCycle) return false;
  if (!usedTagOverride) return setup?.membershipYearProrateBasePrice !== false;

  const mode = resolveTagProrationMode(setup);
  if (mode === 'all') return true;
  if (mode === 'none') return false;

  const selected = Array.isArray(setup?.membershipYearProratedTagNames)
    ? setup.membershipYearProratedTagNames.filter((name) => typeof name === 'string')
    : [];
  return Boolean(tagName && selected.includes(tagName));
}
